import type {DiffEntry, Suggestion} from '@github-ui/diff-lines/conversation-types'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

// -- String-literal union schemas --

const FreshnessSchema = Type.Union([Type.Literal('current'), Type.Literal('stale'), Type.Literal('pending')])
export type Freshness = Type.Static<typeof FreshnessSchema>

const FindingResolvedReasonSchema = Type.Union([
  Type.Literal('addressed'),
  Type.Literal('wont_fix'),
  Type.Literal('incorrect'),
])
export type FindingResolvedReason = Type.Static<typeof FindingResolvedReasonSchema>

const FindingHeadShaRelationshipSchema = Type.Union([Type.Literal('current'), Type.Literal('prior')])
export type FindingHeadShaRelationship = Type.Static<typeof FindingHeadShaRelationshipSchema>

const FindingSourceLifecycleSchema = Type.Union([
  Type.Literal('durable'),
  Type.Literal('snapshot'),
  Type.Literal('unknown'),
])
export type FindingSourceLifecycle = Type.Static<typeof FindingSourceLifecycleSchema>

const FindingSourceKindSchema = Type.Union([
  Type.Literal('check_suite'),
  Type.Literal('check_run'),
  Type.Literal('pull_request_backfill'),
  Type.Literal('pr_review_comment'),
  Type.Literal('pr_timeline_comment'),
  Type.Literal('ccr_review_comment'),
  Type.Literal('ci_analysis'),
  Type.Literal('ci_failure_analysis'),
  Type.Literal('code_scanning'),
])
export type FindingSourceKind = Type.Static<typeof FindingSourceKindSchema>

const MergeReadinessSchema = Type.Union([
  Type.Literal('ready'),
  Type.Literal('requires_changes'),
  Type.Literal('too_complex'),
  Type.Literal('unknown'),
])
export type MergeReadiness = Type.Static<typeof MergeReadinessSchema>

// -- Nested object schemas --

const SourceReferenceSchema = Type.Object({
  source: FindingSourceKindSchema,
  entityId: Type.String(),
  url: Type.Optional(Type.String()),
  toolName: Type.Optional(Type.String()),
  authorLogin: Type.Optional(Type.String()),
  authorAvatarUrl: Type.Optional(Type.String()),
  authorIsBot: Type.Optional(Type.Boolean()),
  linkLabel: Type.Optional(Type.String()),
})
export type SourceReference = Type.Static<typeof SourceReferenceSchema>

// DiffEntry is an external type from @github-ui/diff-lines. Type.Unsafe preserves
// the real TS type for downstream consumers while validating as Array(Unknown) at runtime.
const DiffEntriesSchema = Type.Unsafe<DiffEntry[]>(Type.Array(Type.Unknown()))

const CommentPreviewSuggestionSchema = Type.Object({
  diffEntries: DiffEntriesSchema,
  state: Type.String(),
})
export type CommentPreviewSuggestion = Suggestion & {state: string}

const CommentPreviewDataSchema = Type.Object({
  author: Type.Object({
    login: Type.String(),
    avatarUrl: Type.String(),
    isBot: Type.Boolean(),
  }),
  title: Type.Optional(Type.String()),
  bodyHTML: Type.String(),
  severity: Type.Optional(Type.String()),
  source: Type.Optional(Type.String()),
  url: Type.Optional(Type.String()),
  createdAt: Type.Optional(Type.String()),
  diffHunk: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  commentDatabaseId: Type.Number(),
  basePath: Type.String(),
  suggestion: Type.Optional(Type.Union([CommentPreviewSuggestionSchema, Type.Null()])),
})
export type CommentPreviewData = Type.Static<typeof CommentPreviewDataSchema>

const FindingSchema = Type.Object({
  id: Type.String(),
  filePath: Type.String(),
  startLine: Type.Number(),
  endLine: Type.Number(),
  startColumn: Type.Number(),
  endColumn: Type.Number(),
  message: Type.String(),
  ruleId: Type.String(),
  category: Type.String(),
  severity: Type.String(),
  groupTitle: Type.String(),
  suggestion: Type.String(),
  approvalReadiness: Type.String(),
  resolvedReason: Type.Optional(Type.Union([FindingResolvedReasonSchema, Type.Null()])),
  resolvedAt: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  resolverId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  ccaTaskId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  sourceReferences: Type.Optional(Type.Array(SourceReferenceSchema)),
  // fileUrl is the UI contract for rendering filePath as a repo file location.
  // If fileUrl is absent, filePath may be source/job context and should not be rendered as a file location.
  fileUrl: Type.Optional(Type.String()),
  commentPreviews: Type.Optional(Type.Array(CommentPreviewDataSchema)),
  originHeadSha: Type.Optional(Type.String()),
  headShaRelationship: Type.Optional(FindingHeadShaRelationshipSchema),
  sourceLifecycle: Type.Optional(FindingSourceLifecycleSchema),
})
export type Finding = Type.Static<typeof FindingSchema>

const FindingsPayloadSchema = Type.Object({
  findings: Type.Array(FindingSchema),
  freshness: FreshnessSchema,
  version: Type.Number(),
  processedVersion: Type.Number(),
  pullRequestBasePath: Type.String(),
  modelsUrl: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  mergeReadiness: Type.Optional(Type.Union([MergeReadinessSchema, Type.Null()])),
  showFeedbackBanner: Type.Optional(Type.Boolean()),
  feedbackBannerDismissPath: Type.Optional(Type.Union([Type.String(), Type.Null()])),
})
export type FindingsPayload = Type.Static<typeof FindingsPayloadSchema>

// -- Persister validator --
// The schema requires payload to be a non-null FindingsPayload so that
// 404 / "not found" states (payload: null) are not served from cache.
export const FindingsQueryDataSchema = Type.Object({
  payload: FindingsPayloadSchema,
  retryAfterMs: Type.Number(),
})

// The runtime type allows payload to be null (returned by queryFn on 404).
// The persister schema above intentionally rejects null payloads so they are not cached.
export interface FindingsQueryData {
  payload: FindingsPayload | null
  retryAfterMs: number
}

export const FindingsQueryDataValidator = Compile(FindingsQueryDataSchema)
